import React, { useEffect, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import DataTable from 'react-data-table-component';
import { Link } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import Tooltip from 'react-simple-tooltip';

import { useAccount } from 'wagmi';

import { useGlobalContext } from '../../AppContext';
import { calculateColumnWidth, parseAmount, parseMoney } from '../../utils/utils';
import { BigButton } from '../../components/BigButton';
import { BigMenu } from '../../components/BigMenu';
import { Dropdown } from '../../components/Dropdown';
import { treeMapOptions } from '../HeatMap/graphData';
import { donutOptions, lineOptions } from './graphData';

import '../../router/HeatMap/HeatMap.scss';
import './StatisticsAndFees.scss';

export const StatisticsAndFees = () => {
  const [theme, setTheme] = useState('light');

  useEffect(() => {
    const handleThemeChange = () => {
      const newTheme = document.body.classList.contains('dark') ? 'light' : 'dark';
      setTheme(newTheme);
    };

    handleThemeChange();

    const observer = new MutationObserver(handleThemeChange);
    const config = { attributes: true, attributeFilter: ['class'] };
    observer.observe(document.body, config);

    return () => {
      observer.disconnect();
    };
  }, []);
  const SparklineTheme = document.body.classList.contains("dark") ? "000000" : "fffaed";

  const { isMobile, config, address } = useGlobalContext();
  const { address: account } = useAccount();
  const history = useLocation().state as { type: string };
  const [heatMapData, setHeatMapData] = useState<any>();
  const [selectedTypeHeatmap, setSelectedTypeHeatmap] = useState<string>('market');

  const [selectedType, setSelectedType] = useState<string>('statistics');
  const [chartType, setChartType] = useState<string>('chart-1');

  const [apexChartOptions, setApexChartOptions] = useState<any>(false);
  const [apexChartSeries, setApexChartSeries] = useState<any>(false);
  const [showChart, setShowChart] = useState(false);

  const [loading, setLoading] = useState(true);

  const [showTable, setShowTable] = useState<JSX.Element>(<></>);
  const [tableData, setTableData] = useState<TableDataProps[]>([]);
  const [rawData, setRawData] = useState<any>(false);

  useEffect(() => {
    try {
      if (history.selectedType) {
        setSelectedType(history.selectedType);
      }
    } catch (e) {
      console.log(e.message);
    }
  }, [history]);

  useEffect(() => {
    if (config && (config.moonbeamLP || config.moonriverLP)) {
      setRawData([...config.moonbeamLP, ...config.moonriverLP]);
    } else {
      renderShowTable({});
    }
  }, [config]);

  useEffect(() => {
    renderShowTable(tableData);
  }, [tableData]);

  const renderShowTable = (data: any): void => {
    setShowTable(
      <DataTable
        noDataComponent={
          <div className="data-table-no-record">
            There are no records to display
            <hr />
            <div>
              <small>
                Available liquidity pools will be visible when you have some suitable pairs.
              </small>
            </div>
          </div>
        }
        pagination
        columns={columns}
        data={data}
      />,
    );
  };

  const columns = [
    {
      name: 'Network',
      selector: (row: TableDataProps) => row.name,
      width: calculateColumnWidth('lp', 'network'),
      cell: (row: TableDataProps) => {
        return (
          <>
            <div className="icon-container">
              <img src={`/icons/networks/${row.name.toLowerCase()}.png`} className={'native'} />
            </div>
            {row.name}
          </>
        );
      },
    },
    {
      name: 'Pair',
      width: calculateColumnWidth('lp', 'pair'),
      cell: (row: TableDataProps) => {
        return (
          <div className="pair-container">
            <img src={row.logo0} alt="" /> {row.symbol0} / <img src={row.logo1} alt="" />{' '}
            {row.symbol1}
          </div>
        );
      },
    },
    {
      name: 'Liquidity USD',
      selector: (row: TableDataProps) => row.liquidityUSD,
      cell: (row: TableDataProps) => row.liquidityUSD,
      width: calculateColumnWidth('lp', 'liquidity'),
    },
    {
      name: 'Token#1',
      selector: (row: TableDataProps) => row.token0,
      width: calculateColumnWidth('lp', 'token'),
      cell: (row: TableDataProps) => {
        return (
          <div className="token-container">
            <strong>{row.token0amount}</strong>
            <br />
            <small>({row.token0})</small>
          </div>
        );
      },
    },
    {
      name: 'Token#2',
      selector: (row: TableDataProps) => row.token1,
      width: calculateColumnWidth('lp', 'token'),
      cell: (row: TableDataProps) => {
        return (
          <div className="token-container">
            <strong>{row.token1amount}</strong>
            <br />
            <small>({row.token1})</small>
          </div>
        );
      },
    },
    {
      name: 'Providers',
      selector: (row: TableDataProps) => row.liquidityProviderCount,
      width: calculateColumnWidth('lp', 'providerCount'),
      cell: (row: TableDataProps) => row.liquidityProviderCount,
    },
    {
      name: '',
      selector: (row: TableDataProps) => row.button,
      width: calculateColumnWidth('lp', 'action'),
      cell: (row: TableDataProps) => {
        return (
          <div className="table-actions iconWithText">
            <Tooltip
              placement="left"
              className="tooltip"
              border="#ff6aff"
              background="var(--color-background-main)" 
              color="var(--color-text-main)"
              content={`You can see end update your vesting details`}
            >
              <Link
                target="_blank"
                to={{
                  pathname: `https://analytics.${
                    row.name === 'Moonriver' ? 'solarbeam' : 'solarflare'
                  }.io/pairs/${row.address}`,
                }}
              >
                <img className="" src="/icons/plus.svg" alt="" /> Provide Liquidity
              </Link>
            </Tooltip>
          </div>
        );
      },
    },
  ];

  interface TableDataProps {
    name: string;
    pair: string;
    symbol0: string;
    symbol1: string;
    liquidityUSD: string;
    token0: string;
    logo0: string;
    token0amount: string;
    token1: string;
    logo1: string;
    token1amount: string;
    liquidityProviderCount: number;
    button: boolean;
  }

  useEffect(() => {
    const tmpData: TableDataProps[] = new Array();
    console.log(config.moonbeamLP);
    console.log(config.moonriverLP);
    Object.values(rawData).forEach((item: any) => {
      tmpData.push({
        address: item.id,
        name: item.network,
        symbol0: item.token0.symbol,
        symbol1: item.token1.symbol,
        liquidityUSD: parseMoney(parseFloat(item.reserveUSD), false, 2),
        token0: parseAmount(item.token0.totalLiquidity),
        logo0: item.logo0,
        token0amount: parseAmount(item.token0amount),
        token1: parseAmount(item.token1.totalLiquidity),
        logo1: item.logo1,
        token1amount: parseAmount(item.token1amount),
        liquidityProviderCount: parseAmount(item.liquidityProviderCount),
        button: true,
      });
    });
    setTableData(tmpData);
  }, [rawData]);

  useEffect(() => {
    if (config && config.networks) {
      if (isMobile) {
        treeMapOptions.dataLabels.style.fontSize = '7px';
      }
      if (selectedTypeHeatmap === 'market') {
        const tmp = config.heatMapMarket.map((item: any) => {
          return { x: item.symbol.toUpperCase(), y: item.change };
        });
        setHeatMapData([
          {
            data: tmp.filter((item: any) => {
              return Math.abs(item.y) > 2;
            }),
          },
        ]);
      } else {
        const tmp = config.heatMapPortfolio.map((item: any) => {
          return { x: item.symbol.toUpperCase(), y: item.change };
        });
        setHeatMapData([
          {
            data: tmp.filter((item: any) => {
              return Math.abs(item.y) > 2;
            }),
          },
        ]);
      }
    } else {
      setHeatMapData(false);
    }
  }, [config, selectedTypeHeatmap]);

  useEffect(() => {
    if (config && config.networks) {
      setLoading(false);
    } else {
      setLoading(true);
    }
  }, [config]);

  const onClickHandler = (e: any) => {
    for (let i = 0; i < e.target.parentNode.childNodes.length; i++) {
      e.target.parentNode.childNodes[i].classList.remove('active');
    }
    e.target.classList.toggle('active');
    if (chartType !== e.target.classList[0]) {
      setShowChart(false);
      setChartType(e.target.classList[0]);
    }
  };

  useEffect(() => {
    if (config && selectedType == 'statistics') {
      if (chartType == 'chart-2') {
        // draw donut graph
        setApexChartOptions({
          ...donutOptions,
          labels: Object.keys(config.stats.top10Percentage).map((key: string) => key.toUpperCase()),
          //colors: Object.keys(config.stats.top10Percentage).map((key: string) => randomColor()),
          colors: [
            '#ff6aff',
            '#d5fe88',
            '#f71dd8',
            '#4effc8',
            '#6ff5ff',
            '#e32a73',
            '#ffffff',
            '#f5841f',
            '#3c3956',
            '#1c1919',
          ],
        });
        setApexChartSeries(
          Object.keys(config.stats.top10Percentage).map(
            (key: string) => config.stats.top10Percentage[key],
          ),
        );
      } else if (chartType == 'chart-1') {
        // draw line graph
        setApexChartOptions({
          ...lineOptions,
          //colors: Object.keys(config.networks).map((key: string) => config.networks[key].color),
          colors: ['#ff6aff', '#e32a73'],
        });
        setApexChartSeries([
          {
            name: 'Marketcap',
            data: Object.values(config.stats.marketcap[0].data).map((item: any) => {
              return parseInt((item[1] / 1000000).toFixed());
            }),
          },
          {
            name: 'Total Volume',
            data: Object.values(config.stats.marketcap[1].data).map((item: any) => {
              return parseInt((item[1] / 1000000).toFixed());
            }),
          },
        ]);
        /*
        setApexChartSeries(
          Object.keys(config.networks).map((key: string) => {
            return {
              name: config.networks[key].name,
              data: config.networks[key].graphData,
            };
          }),
        );
        */
      }
      setShowChart(true);
    } else {
      setShowChart(false);
    }
  }, [chartType, config]);

  const changeChart = (value: string) => {
    if (chartType !== value) {
      setShowChart(false);
      setChartType(value);
    }
  };

  return (
    <div className="statistics-fees">
      <BigMenu horizontal={true} />
      <div className="button-container mt-5">
        <div onClick={() => setSelectedType('statistics')}>
          <BigButton
            size="small"
            name="Stats"
            type={`${selectedType === 'statistics' ? 'light' : 'dark'}`}
          />
        </div>

        <div onClick={() => setSelectedType('heatmap')}>
          <BigButton
            size="small"
            name="Heatmap"
            type={`${selectedType === 'heatmap' ? 'light' : 'dark'}`}
          />
        </div>

        <div onClick={() => setSelectedType('lp')}>
          <BigButton size="small" name="LP" type={`${selectedType === 'lp' ? 'light' : 'dark'}`} />
        </div>
      </div>
      {!loading && selectedType === 'statistics' && (
        <div className="statistics-container mt-5">
          {isMobile ? (
            <div className="dashboard-widget-title color-five d-flex statsDropdown">
              <Dropdown
                change={(value: string) => changeChart(value)}
                color="white"
                size="small"
                label="Marketcap"
              >
                <div data-id="chart-1">Marketcap</div>
                <div data-id="chart-2">Top Coins</div>
                <div data-id="chart-3">Overall Stats</div>
              </Dropdown>
            </div>
          ) : (
            <div className="statistics-widget">
              <div onClick={onClickHandler} id="chart1" className="chart-1 active">
                Marketcap
              </div>
              <div onClick={onClickHandler} id="chart2" className="chart-2">
                Top Coins
              </div>
              <div onClick={onClickHandler} id="chart3" className="chart-3">
                Overall Stats
              </div>
            </div>
          )}
          <div className="statistics-content">
            {showChart && chartType === 'chart-1' && (
              <>
                <div className={`chart-container border-five-${theme}`}>
                  <div className="statistics-content-title color-five">
                    Marketcap &amp; Total Volumes
                  </div>
                  <ReactApexChart
                    options={apexChartOptions}
                    series={apexChartSeries}
                    type="line"
                    width={'100%'}
                    height={innerWidth < 1025 ? 100 : 200}
                  />
                </div>
              </>
            )}

            {showChart && chartType === 'chart-2' && (
              <>
                <div className={`token-list-${theme}`}>
                  <div className="statistics-content-title color-five">Trend Searches</div>
                  {config &&
                    config.stats.trendSearch &&
                    Object.values(config.stats.trendSearch)
                      .slice(0, 7)
                      .map((item: any, index: number) => {
                        return (
                          <div key={index} className="token">
                            <div className="token-name">
                              <img src={item.item.small} alt="" className="token-icon" />{' '}
                              {item.item.name}
                            </div>
                            <div className="token-last-price">
                              {parseFloat(item.item.price_btc).toFixed(8)} BTC
                            </div>
                          </div>
                        );
                      })}
                </div>
                <div className="chart-container">
                  <div className="statistics-content-title color-six donut">Top 10 Coins</div>
                  <ReactApexChart
                    options={apexChartOptions}
                    series={apexChartSeries}
                    type="donut"
                    width={'100%'}
                    height={innerWidth < 1025 ? 200 : 300}
                  />
                </div>
              </>
            )}

            {showChart && chartType === 'chart-3' && (
              <div className="statistics-data-content">
                <div className="item">
                  <div className="name">Fear &amp; Greed</div>
                  <div className="value">
                    {config.stats.fearAndGreed[0].value} (
                    {config.stats.fearAndGreed[0].value_classification})
                  </div>
                  <div className="chart">
                    <img
                      src={`https://chart.googleapis.com/chart?chco=e32a73&chf=bg,s,${SparklineTheme}&cht=lc:nda&chds=${Math.min(
                        ...config.stats.fearAndGreed.map((item: any) => item.value),
                      )},${Math.max(
                        ...config.stats.fearAndGreed.map((item: any) => item.value),
                      )}&chs=300x34&chd=t:${config.stats.fearAndGreed.map(
                        (item: any) => item.value,
                      )}`}
                      alt=""
                    />
                  </div>
                </div>
                <div className="item">
                  <div className="name">Marketcap</div>
                  <div className="value">{config.stats.overall.marketcap}</div>
                </div>
                <div className="item">
                  <div className="name">24h Volume</div>
                  <div className="value">{config.stats.overall['24hvolume']}</div>
                </div>
                <div className="item">
                  <div className="name">BTC Dominance</div>
                  <div className="value">{config.stats.overall.dominanceBTC}</div>
                </div>
                <div className="item">
                  <div className="name">ETH Dominance</div>
                  <div className="value">{config.stats.overall.dominanceETH}</div>
                </div>
                <div className="item">
                  <div className="name"># of Exchanges</div>
                  <div className="value">{config.stats.overall.exchanges}</div>
                </div>
                <div className="item">
                  <div className="name">Gas</div>
                  <div className="value">{config.stats.overall.gas}</div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      {!loading && selectedType === 'heatmap' && (
        <>
          <div className="heat-map">
            {account && account === address && (
              <div
                className={`${
                  isMobile
                    ? 'dashboard-widget-title color-fve d-flex statsDropdown'
                    : 'button-container'
                }`}
              >
                <Dropdown
                  change={(value: string) => setSelectedTypeHeatmap(value)}
                  color="white"
                  size="small"
                  label="Market"
                >
                  <div data-id="market">Market</div>
                  {account && account === address && <div data-id="portfolio">Portfolio</div>}
                </Dropdown>
              </div>
            )}
            <div className="heat-map-container">
              <div className="heat-map-chart mt-5">
                {config && heatMapData && (
                  <>
                    <div className={`custom-table-${theme} mt-5 padding-left`}>
                      <div className="table-border-top" />
                      <div>
                        <ReactApexChart
                          options={treeMapOptions}
                          series={heatMapData}
                          type="treemap"
                          width={isMobile ? 310 : 921}
                          height={isMobile ? 220 : 620}
                        />
                      </div>
                      <div className="table-border-bottom" />
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </>
      )}
      {!loading && selectedType === 'lp' && (
        <>
          <div className="lp-container">
            <div className="table-container mt-5">
              <div className={`custom-table-${theme} padding-left`}>
                <div className="table-border-top" />
                {showTable}
                <div className="table-border-bottom" />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
